import React, { useState, useEffect } from 'react';
import inventoryApi from '../../../services/inventoryApi';

const ItemDetailsDrawer = ({
  itemId,
  store,
  onClose,
  warehouses = []
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetails = async () => {
      if (!itemId) return;
      try {
        setLoading(true);
        setError('');
        const res = await inventoryApi.getItemLocationDetails(itemId, { store });
        setData(res.data);
      } catch (err) {
        console.error('Failed to load item details:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [itemId, store]);

  if (!itemId) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-sm flex justify-end transition-opacity">
      <div className="w-full max-w-xl bg-white shadow-2xl h-full flex flex-col transform transition-transform duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold">Item Storage &amp; Stock Details</h3>
              <p className="text-xs text-indigo-100">Storage locations, rack levels &amp; movements</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-sm">Loading storage details...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {data && !loading && (
            <>
              {/* Item Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex gap-4 items-center">
                <div className="w-20 h-20 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {data.item.imageUrl ? (
                    <img src={data.item.imageUrl} alt={data.item.name} className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-bold text-gray-900 truncate">{data.item.name}</h4>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 text-xs text-gray-600">
                    <div>SKU: <span className="font-semibold text-gray-900">{data.item.sku}</span></div>
                    <div>Barcode: <span className="font-semibold text-gray-900">{data.item.barcode}</span></div>
                    <div>Category: <span className="font-semibold text-gray-900">{data.item.category}</span></div>
                    <div>Unit: <span className="font-semibold text-gray-900">{data.item.unit}</span></div>
                  </div>
                </div>
              </div>

              {/* Stock Overview Cards */}
              <div>
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Inventory Stock Breakdown
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
                    <span className="block text-xs font-semibold text-blue-700">Total Stock</span>
                    <span className="text-xl font-extrabold text-blue-900">
                      {data.stockSummary.totalStock} {data.item.unit}
                    </span>
                  </div>

                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-center">
                    <span className="block text-xs font-semibold text-purple-700">Allocated to Racks</span>
                    <span className="text-xl font-extrabold text-purple-900">
                      {data.stockSummary.totalAllocated} {data.item.unit}
                    </span>
                  </div>

                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                    <span className="block text-xs font-semibold text-emerald-700">Unallocated</span>
                    <span className="text-xl font-extrabold text-emerald-900">
                      {data.stockSummary.unallocatedStock} {data.item.unit}
                    </span>
                  </div>
                </div>
              </div>

              {/* Specific Storage Locations Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Rack Storage Locations ({data.allocations.length})
                  </h4>
                </div>

                {data.allocations.length === 0 ? (
                  <div className="p-6 text-center bg-gray-50 border border-dashed border-gray-200 rounded-xl text-gray-500 text-xs">
                    This item is not currently stored on any racks. It resides in bulk store or unallocated holding.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50 text-gray-600 font-semibold uppercase">
                        <tr>
                          <th className="px-3 py-2.5 text-left">Store / Warehouse</th>
                          <th className="px-3 py-2.5 text-left">Rack</th>
                          <th className="px-3 py-2.5 text-center">Level</th>
                          <th className="px-3 py-2.5 text-center">Position</th>
                          <th className="px-3 py-2.5 text-right">Quantity</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {data.allocations.map((alloc, idx) => {
                          const storeObj = warehouses.find(w => w._id === alloc.store);
                          return (
                            <tr key={idx} className="hover:bg-indigo-50/40 transition-colors">
                              <td className="px-3 py-2.5 text-gray-900 font-medium">
                                {storeObj ? storeObj.name : 'Store'}
                              </td>
                              <td className="px-3 py-2.5 text-indigo-700 font-bold">
                                {alloc.rackNumber}
                                {alloc.zone && <span className="text-[10px] text-gray-500 font-normal"> (Z:{alloc.zone})</span>}
                              </td>
                              <td className="px-3 py-2.5 text-center text-purple-700 font-semibold">
                                L{alloc.levelNumber}
                              </td>
                              <td className="px-3 py-2.5 text-center text-emerald-700 font-bold">
                                {alloc.positionNumber}
                              </td>
                              <td className="px-3 py-2.5 text-right font-extrabold text-gray-900">
                                {alloc.quantity} {alloc.unit}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Recent Movements */}
              <div>
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Recent Stock Movements
                </h4>

                {data.recentMovements.length === 0 ? (
                  <div className="p-4 bg-gray-50 border border-gray-100 rounded-xl text-center text-xs text-gray-500">
                    No recent movement records found.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.recentMovements.map((m, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-gray-900">{m.movementNumber || 'Movement'}</span>
                          <span className="ml-2 text-gray-500">({m.type})</span>
                          <div className="text-[10px] text-gray-400">
                            {new Date(m.date).toLocaleString()}
                          </div>
                        </div>
                        <div className={`font-bold ${m.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                          {m.direction === 'in' ? '+' : '-'}{m.quantity}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg shadow-sm transition-colors"
          >
            Close Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailsDrawer;
